import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calculator, TrendingUp, Award, DollarSign } from 'lucide-react';

const DEFAULT_SLABS = [
  { id: 1, minEnrollments: 0, maxEnrollments: 5, rate: 3, description: 'Starter' },
  { id: 2, minEnrollments: 6, maxEnrollments: 15, rate: 10, description: 'Bronze' },
  { id: 3, minEnrollments: 16, maxEnrollments: 30, rate: 15, description: 'Silver' },
  { id: 4, minEnrollments: 31, maxEnrollments: 50, rate: 20, description: 'Gold' },
  { id: 5, minEnrollments: 51, maxEnrollments: 100, rate: 25, description: 'Platinum' },
  { id: 6, minEnrollments: 101, maxEnrollments: null, rate: 50, description: 'Diamond' }
];

export default function SlabBasedCalculator({ slabs = DEFAULT_SLABS, agentEnrollments = 0, onCalculate }) {
  const [enrollments, setEnrollments] = useState(agentEnrollments);
  const [baseAmount, setBaseAmount] = useState(25000);
  const [calculation, setCalculation] = useState(null);

  const calculateCommission = (enrollmentCount, amount) => {
    // Find the appropriate slab
    const applicableSlab = slabs.find(slab => {
      if (slab.maxEnrollments === null) {
        return enrollmentCount >= slab.minEnrollments;
      }
      return enrollmentCount >= slab.minEnrollments && enrollmentCount <= slab.maxEnrollments;
    });

    if (!applicableSlab) {
      return {
        slab: null,
        rate: 0,
        commission: 0,
        tier: 'No Tier',
        nextTier: null,
        enrollmentsToNext: 0
      };
    }

    const commission = (amount * applicableSlab.rate) / 100;
    
    // Find next tier
    const nextSlab = slabs.find(slab => slab.minEnrollments > enrollmentCount);
    const enrollmentsToNext = nextSlab ? nextSlab.minEnrollments - enrollmentCount : 0;

    return {
      slab: applicableSlab,
      rate: applicableSlab.rate,
      commission: commission,
      tier: applicableSlab.description,
      nextTier: nextSlab?.description || null,
      enrollmentsToNext: enrollmentsToNext
    };
  };

  useEffect(() => {
    const result = calculateCommission(enrollments, baseAmount);
    setCalculation(result);
    onCalculate?.(result);
  }, [enrollments, baseAmount, slabs]);

  const handleCalculate = () => {
    const result = calculateCommission(enrollments, baseAmount);
    setCalculation(result);
    onCalculate?.(result);
  };

  const getTierColor = (tier) => {
    const colors = {
      'Starter': 'bg-gray-100 text-gray-800',
      'Bronze': 'bg-orange-100 text-orange-800',
      'Silver': 'bg-gray-200 text-gray-800',
      'Gold': 'bg-yellow-100 text-yellow-800',
      'Platinum': 'bg-purple-100 text-purple-800',
      'Diamond': 'bg-blue-100 text-blue-800'
    };
    return colors[tier] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Calculator Input */}
      <Card className="neumorphic rounded-3xl">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-neumorphic flex items-center gap-2">
            <Calculator className="w-5 h-5 text-indigo-600" />
            Slab-Based Commission Calculator
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-neumorphic font-medium">Number of Enrollments</Label>
              <Input
                type="number"
                value={enrollments}
                onChange={(e) => setEnrollments(parseInt(e.target.value) || 0)}
                className="neumorphic-inset rounded-xl border-0 bg-gray-200"
                placeholder="Enter enrollment count"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-neumorphic font-medium">Base Amount per Student ($)</Label>
              <Input
                type="number"
                value={baseAmount}
                onChange={(e) => setBaseAmount(parseFloat(e.target.value) || 0)}
                className="neumorphic-inset rounded-xl border-0 bg-gray-200"
                placeholder="Enter base amount"
              />
            </div>
          </div>
          <Button
            onClick={handleCalculate}
            className="neumorphic rounded-xl px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white"
          >
            Calculate Commission
          </Button>
        </CardContent>
      </Card>

      {/* Calculation Results */}
      {calculation && (
        <Card className="neumorphic rounded-3xl">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-neumorphic flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Commission Calculation Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Current Tier */}
              <div className="neumorphic-inset rounded-2xl p-4 text-center">
                <Award className="w-8 h-8 mx-auto mb-2 text-indigo-600" />
                <p className="text-sm text-gray-500 mb-1">Current Tier</p>
                <Badge className={`${getTierColor(calculation.tier)} border-0 text-lg px-3 py-1`}>
                  {calculation.tier}
                </Badge>
              </div>

              {/* Commission Rate */}
              <div className="neumorphic-inset rounded-2xl p-4 text-center">
                <div className="w-8 h-8 mx-auto mb-2 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 font-bold">%</span>
                </div>
                <p className="text-sm text-gray-500 mb-1">Commission Rate</p>
                <p className="text-2xl font-bold text-green-600">{calculation.rate}%</p>
              </div>

              {/* Total Commission */}
              <div className="neumorphic-inset rounded-2xl p-4 text-center">
                <DollarSign className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                <p className="text-sm text-gray-500 mb-1">Total Commission</p>
                <p className="text-2xl font-bold text-purple-600">
                  ${calculation.commission.toLocaleString()}
                </p>
              </div>

              {/* Next Tier Progress */}
              <div className="neumorphic-inset rounded-2xl p-4 text-center">
                <TrendingUp className="w-8 h-8 mx-auto mb-2 text-orange-600" />
                <p className="text-sm text-gray-500 mb-1">To Next Tier</p>
                {calculation.nextTier ? (
                  <>
                    <p className="text-lg font-bold text-orange-600">
                      {calculation.enrollmentsToNext} more
                    </p>
                    <p className="text-xs text-gray-400">to reach {calculation.nextTier}</p>
                  </>
                ) : (
                  <p className="text-lg font-bold text-green-600">Max Tier!</p>
                )}
              </div>
            </div>

            {/* Calculation Breakdown */}
            <div className="mt-6 neumorphic-inset rounded-2xl p-4">
              <h4 className="font-semibold text-neumorphic mb-3">Calculation Breakdown</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Enrollments:</span>
                  <span className="font-mono">{enrollments}</span>
                </div>
                <div className="flex justify-between">
                  <span>Base Amount per Student:</span>
                  <span className="font-mono">${baseAmount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Total Base Amount:</span>
                  <span className="font-mono">${(baseAmount * enrollments).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Commission Rate ({calculation.tier}):</span>
                  <span className="font-mono">{calculation.rate}%</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-bold">
                  <span>Total Commission:</span>
                  <span className="font-mono text-green-600">${calculation.commission.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Slab Reference Table */}
      <Card className="neumorphic rounded-3xl">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-neumorphic">Commission Slab Reference</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3">
            {slabs.map((slab) => (
              <div
                key={slab.id}
                className={`neumorphic-inset rounded-xl p-3 flex items-center justify-between ${
                  calculation?.slab?.id === slab.id ? 'ring-2 ring-indigo-500 bg-indigo-50' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <Badge className={`${getTierColor(slab.description)} border-0`}>
                    {slab.description}
                  </Badge>
                  <span className="font-mono text-sm">
                    {slab.maxEnrollments === null 
                      ? `${slab.minEnrollments}+ enrollments`
                      : `${slab.minEnrollments}-${slab.maxEnrollments} enrollments`
                    }
                  </span>
                </div>
                <span className="font-bold text-green-600">{slab.rate}%</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}